import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi } from '../api/userApi';
import toast from 'react-hot-toast';

// Query key constant to avoid typos
const QUERY_KEY = ['userPreferences'];

// React Query v5+ prefers the single-object syntax. Using it ensures we share the
// same QueryClient instance across the entire app and avoids the duplicated-library
// edge case that was crashing the notifications page.
export const useUserPreferencesQuery = () => {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: userApi.getPreferences,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
};

export const useUpdateUserPreferencesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Unique key helps with devtools but isn't required
    mutationKey: ['updateUserPreferences'],
    mutationFn: (payload) => userApi.updatePreferences(payload),

    // Optimistic cache update before the request is sent
    onMutate: async (payload) => {
      await queryClient.cancelQueries(QUERY_KEY);
      const previousData = queryClient.getQueryData(QUERY_KEY);
      if (previousData) {
        queryClient.setQueryData(QUERY_KEY, (old) => ({
          ...old,
          ...payload,
          notifications: {
            ...old.notifications,
            ...payload.notifications,
          },
        }));
      }
      return { previousData };
    },

    onError: (_err, _payload, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(QUERY_KEY, context.previousData);
      }
      toast.error('Failed to save preferences');
    },

    onSuccess: () => {
      toast.success('Preferences saved');
    },

    onSettled: () => {
      queryClient.invalidateQueries(QUERY_KEY);
    },
  });
}; 